/**
 * AuthContext.js — global auth state.
 *
 * Token storage:
 *   rememberMe=true  → localStorage  (survives browser close)
 *   rememberMe=false → sessionStorage (cleared on tab close)
 *
 * On load: reads token from either storage, calls /me to validate.
 * If /me fails for any reason, clears token and shows login — never crashes.
 */

import { createContext, useContext, useState, useEffect } from "react";
import { getMe } from "../services/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    if (!token) {
      setLoading(false);
      return;
    }
    getMe()
      .then(setUser)
      .catch(() => {
        localStorage.removeItem("token");
        sessionStorage.removeItem("token");
      })
      .finally(() => setLoading(false));
  }, []);

  function loginSuccess(token, userData, rememberMe = false) {
    // Clear both storages first to avoid stale tokens
    localStorage.removeItem("token");
    sessionStorage.removeItem("token");

    if (rememberMe) {
      localStorage.setItem("token", token);
    } else {
      sessionStorage.setItem("token", token);
    }
    setUser(userData);
  }

  function logout() {
    localStorage.removeItem("token");
    sessionStorage.removeItem("token");
    setUser(null);
  }

  // Refresh user from backend (e.g. after profile edit)
  async function refreshUser() {
    try {
      const u = await getMe();
      setUser(u);
    } catch (_) {}
  }

  return (
    <AuthContext.Provider value={{ user, loading, loginSuccess, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
